import { Link } from "react-router-dom";
import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import type { StravaRun } from "@/api/orval-types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/error-state";
import { useGetRunTotals, useListStravaRuns } from "@/api/orval";

const columns: ColumnDef<StravaRun, any>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: "Name",
  },
  {
    accessorKey: "start_date_local",
    header: "Date",
    cell: ({ row }) => {
      const value = row.getValue("start_date_local");
      const date = value ? new Date(value as string) : null;
      return date && !isNaN(date.getTime()) ? date.toLocaleDateString() : "-";
    },
  },
  {
    accessorKey: "distance",
    header: "Distance (km)",
    cell: ({ row }) => {
      const meters = (row.getValue("distance") as number | null) ?? 0;
      return (meters / 1000).toFixed(2);
    },
  },
  {
    accessorKey: "moving_time",
    header: "Time",
    cell: ({ row }) => {
      const seconds = row.getValue("moving_time") as number | null;
      if (seconds == null) return "-";
      return new Date(seconds * 1000).toISOString().substring(11, 19);
    },
  },
  {
    accessorKey: "total_elevation_gain",
    header: "Elevation (m)",
    cell: ({ row }) => {
      const gain = (row.getValue("total_elevation_gain") as number | null) ?? 0;
      return gain.toFixed(0);
    },
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const run = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() =>
                navigator.clipboard.writeText(run.strava_id.toString())
              }
            >
              Copy run ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Link
                to={`https://www.strava.com/activities/${run.strava_id}`}
                target="_blank"
              >
                View on Strava
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

export const Runs: React.FC = () => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [pageIndex, setPageIndex] = React.useState(0);
  const PAGE_SIZE = 10;

  const {
    data: stravaRuns,
    isFetching: isRunsFetching,
    isError: isRunsError,
  } = useListStravaRuns(
    { limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE },
    // Keep the current page visible while the next one loads.
    { query: { placeholderData: keepPreviousData } },
  );
  const { data: stravaTotals, isLoading: isTotalsLoading } = useGetRunTotals();

  const rows = stravaRuns ?? [];
  const canPrev = pageIndex > 0;
  const canNext = rows.length === PAGE_SIZE;

  const table = useReactTable({
    data: rows,
    columns,
    manualPagination: true,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  if (isRunsError) {
    return (
      <div className="w-full p-6">
        <ErrorState
          title="Couldn't load your runs"
          description="Something went wrong fetching your runs. Try reloading the page."
          action={
            <Button onClick={() => window.location.reload()}>Reload</Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Distance</CardTitle>
          </CardHeader>
          <CardContent>
            {isTotalsLoading ? (
              <>
                <Skeleton className="h-8 w-24" />
                <Skeleton className="mt-2 h-3 w-20" />
                <Skeleton className="mt-2 h-2 w-full" />
              </>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {(stravaTotals?.total_distance ?? 0).toFixed(1)} km
                </div>
                <div className="text-xs text-muted-foreground">
                  Goal: 2000 km
                </div>
                <Progress
                  value={((stravaTotals?.total_distance ?? 0) / 2000.0) * 100}
                  className="mt-2"
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time</CardTitle>
          </CardHeader>
          <CardContent>
            {isTotalsLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-2xl font-bold">
                {formatDuration(stravaTotals?.total_time ?? 0)}
              </div>
            )}
            <div className="text-xs text-muted-foreground">This year</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Days active</CardTitle>
          </CardHeader>
          <CardContent>
            {isTotalsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">
                {stravaTotals?.days_active ?? 0}
              </div>
            )}
            <div className="text-xs text-muted-foreground">This year</div>
          </CardContent>
        </Card>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-left">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isRunsFetching ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_col, j) => (
                    <TableCell key={j} className="text-left">
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="text-left">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No runs here yet. Connect Strava and your runs will show up
                  automatically.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Page {pageIndex + 1}
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            disabled={!canPrev || isRunsFetching}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex((p) => p + 1)}
            disabled={!canNext || isRunsFetching}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
